import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { testimonials } from '@/mocks/movies';
import { useEffect, useRef } from 'react';

export default function AboutPage() {
  const formSubmittedRef = useRef(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '麻花影视',
    url: 'https://mahua.example.com',
    description: '麻花影视汇集多个特色影视频道，提供高清在线观影体验。',
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formSubmittedRef.current) return;

    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      return;
    }

    const submitAddr = 'https://readdy.ai/api/form/d9n4jggbdkpk99j7ggag';

    formSubmittedRef.current = true;
    try {
      const formData = new FormData(form);
      if (honeypot) formData.delete('phone_alt');
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        alert('感谢您的留言！我们会尽快回复您。');
        form.reset();
      } else {
        alert('提交失败，请稍后重试。');
      }
    } catch {
      alert('网络错误，请稍后重试。');
    } finally {
      formSubmittedRef.current = false;
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="麻花影视 - 了解我们的品牌故事、用户口碑评价及联系方式。汇集四虎影视、影视飓风、番茄影视、大叔影视等特色频道，专注高清在线观影体验。"
        keywords="麻花影视,关于我们,品牌介绍,联系方式,在线影视"
        canonicalUrl="/about"
        schema={orgSchema}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            </div>

            <section className="mb-12">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                关于麻花影视
              </h1>
              <div className="bg-background-100 rounded-xl p-6 md:p-8 space-y-4">
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                  <strong>麻花影视</strong>成立于2024年，是一家专注于影视内容聚合与推荐的在线平台。我们致力于为广大影视爱好者提供<strong>高清流畅</strong>的在线观影体验，汇集了四虎影视、影视飓风、番茄影视、大叔影视、桃花影视、大象影视、草民影视、麻豆影视等八大特色频道。
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                  每个频道都有其独特的风格和定位——从热血澎湃的动作大片到温暖治愈的文艺佳作，从脑洞大开的科幻巨制到真实有力的纪录影像，从适合全家观看的动画电影到碎片时间最佳的短剧精选。无论您是哪种类型的影迷，都能在麻花影视找到属于自己的观影天地。
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                  我们相信，好的影视内容能够触动心灵、开阔视野、连接情感。麻花影视将持续优化内容服务，为您带来更多优质影视资源。
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {testimonials.map((t) => (
                  <div key={t.id} className="bg-background-100 rounded-xl p-5 md:p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: t.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                <div className="md:col-span-2 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-500">contact@mahua.example.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-accent-500 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h4>
                      <p className="text-sm text-foreground-500">400-888-6666</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-500 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                      <p className="text-sm text-foreground-500">北京市朝阳区望京科技园A座1808室</p>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-3">
                  <form onSubmit={handleSubmit} data-readdy-form="" className="bg-background-100 rounded-xl p-5 md:p-6 space-y-4">
                    <h4 className="text-base font-semibold text-foreground-900 mb-2">给我们留言</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                        <input
                          id="about-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                        <input
                          id="about-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1">主题</label>
                      <input
                        id="about-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入留言主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="anti-spam-field"
                    />
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      提交留言
                    </button>
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}